// src/hooks/useInvoices.js
import { useState, useCallback } from 'react';
import { InvoiceAPI } from '../services/api/invoiceAPI';

/**
 * Hook for managing invoice fetching and state
 * Fetches invoices by date range from PostgreSQL via Firebase function
 */
export function useInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch invoices by date range
  const fetchInvoices = useCallback(async (startDate, endDate) => {
    if (!startDate || !endDate) {
      setError('תאריכי התחלה וסיום נדרשים');
      return;
    }

    // Validate date range
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start > end) {
      setError('תאריך התחלה חייב להיות לפני תאריך הסיום');
      return;
    }

    // Check if range is not too large (max 1 year)
    const maxRange = 365 * 24 * 60 * 60 * 1000; // 1 year in milliseconds
    if (end - start > maxRange) {
      setError('טווח התאריכים לא יכול להיות יותר משנה');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Hook: Fetching invoices for date range', {
        startDate,
        endDate
      });

      const result = await InvoiceAPI.fetchInvoicesByDateRange(startDate, endDate);

      const invoiceData = result.map(invoice => ({
        id: invoice.id,
        invoiceUrl: invoice.invoice_url,
        invoiceNumber: invoice.invoice_number,
        invoiceCreatedAt: invoice.invoice_created_at,
        customerName: invoice.customer_name,
        serviceNames: invoice.service_names,
        price: invoice.price,
        appointmentCount: invoice.appointment_count,
        hasOutsideRange: invoice.has_outside_range,
        isBatch: invoice.is_batch
      }));

      // Data is already sorted by invoice creation date DESC from the backend

      console.log('Hook: Successfully loaded invoices', {
        count: invoiceData.length,
        dateRange: `${startDate} to ${endDate}`
      });

      setInvoices(invoiceData);

    } catch (err) {
      console.error('Hook: Error fetching invoices:', {
        error: err.message,
        startDate,
        endDate
      });
      setError(err.message);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setError('');
  }, []);

  // Clear invoices function
  const clearInvoices = useCallback(() => {
    setInvoices([]);
    setError('');
  }, []);

  return {
    invoices,
    loading,
    error,
    fetchInvoices,
    clearError,
    clearInvoices
  };
}